if (!customElements.get('vawcroft-motion-hero')) {
  customElements.define('vawcroft-motion-hero', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.slides = [...this.querySelectorAll('[data-vaw-slide]')];
      this.dots = [...this.querySelectorAll('[data-vaw-dot]')];
      this.caption = this.querySelector('[data-vaw-caption]');
      this.toggle = this.querySelector('[data-vaw-toggle]');
      this.index = 0;
      this.targetIndex = 0;
      this.media = this.querySelector('.vaw-motion__media');
      this.gesture = null;
      this.touching = false;
      this.request = 0;
      this.visible = true;
      this.hovering = false;
      this.focused = false;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = this.dataset.autoplay === 'true' && !this.reduced.matches && !navigator.connection?.saveData;
      this.interval = Math.max(5000, Number(this.dataset.interval) || 7000);
      if (this.slides.length < 2) return;
      this.querySelector('[data-vaw-controls]').hidden = false;
      const options = { signal: this.abort.signal };
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
        this.playing = false;
        this.updateControl();
        this.show(index);
      }, options));
      this.media.addEventListener('dragstart', event => event.preventDefault(), options);
      this.media.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || this.gesture) return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.touching = true;
        this.schedule();
        this.media.setPointerCapture(event.pointerId);
        this.prepare((this.index + 1) % this.slides.length);
        this.prepare((this.index + this.slides.length - 1) % this.slides.length);
      }, options);
      this.media.addEventListener('pointerup', event => this.finishSwipe(event), options);
      this.media.addEventListener('pointercancel', event => this.finishSwipe(event, true), options);
      this.media.addEventListener('lostpointercapture', event => this.finishSwipe(event, true), options);
      this.toggle.addEventListener('click', () => {
        this.playing = !this.playing;
        this.updateControl();
        this.schedule();
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovering = true; this.schedule(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovering = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.reduced.addEventListener('change', () => {
        if (this.reduced.matches) this.playing = false;
        this.updateControl();
        this.schedule();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const target = event.target.closest('[data-vaw-slide]');
        const index = this.slides.indexOf(target);
        if (index >= 0) { this.playing = false; this.updateControl(); this.show(index); }
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.1 });
      this.observer.observe(this);
      this.updateControl();
      this.schedule();
    }
    finishSwipe(event, cancelled = false) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      this.gesture = null;
      this.touching = false;
      if (this.media.hasPointerCapture(event.pointerId)) this.media.releasePointerCapture(event.pointerId);
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      const threshold = Math.min(64, Math.max(36, this.media.clientWidth * 0.1));
      if (cancelled || Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy) * 1.25) {
        this.schedule();
        return;
      }
      this.playing = false;
      this.updateControl();
      const direction = dx < 0 ? 1 : -1;
      this.show((this.targetIndex + direction + this.slides.length) % this.slides.length);
    }
    async prepare(index) {
      const slide = this.slides[index];
      const template = slide.querySelector('template');
      if (template) template.replaceWith(template.content.cloneNode(true));
      const image = slide.querySelector('img');
      if (!image) return false;
      try { await image.decode(); return image.naturalWidth > 0; } catch { return false; }
    }
    async show(index) {
      clearTimeout(this.timer);
      this.targetIndex = index;
      const request = ++this.request;
      const ready = await this.prepare(index);
      if (!this.isConnected || request !== this.request) return;
      if (!ready) { this.playing = false; this.updateControl(); return; }
      this.slides.forEach((slide, item) => {
        slide.classList.toggle('is-current', item === index);
        slide.setAttribute('aria-hidden', String(item !== index));
      });
      this.dots.forEach((dot, item) => {
        if (item === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.caption.textContent = this.slides[index].querySelector('.vaw-motion__caption-source').textContent;
      this.index = index;
      this.schedule();
    }
    updateControl() {
      this.toggle.classList.toggle('is-paused', !this.playing);
      this.toggle.setAttribute('aria-label', this.playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel);
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.playing || !this.visible || this.hovering || this.focused || this.touching || document.hidden) return;
      this.timer = setTimeout(() => this.show((this.index + 1) % this.slides.length), this.interval);
    }
    disconnectedCallback() {
      this.gesture = null;
      this.touching = false;
      clearTimeout(this.timer);
      this.request++;
      this.abort?.abort();
      this.observer?.disconnect();
    }
  });
}
